// ./src/components/features/Vouchers/VoucherItemsEditor/VoucherItemsEditor.tsx
//
// Repeatable editor for a voucher's line-item menu ("what it gives" — e.g.
// Coffee ×1, Popcorn ×1). Controlled: `value` is the item list, `onChange` out.
// Column labels live in a separate header row (not per-first-row) so the trash
// action stays aligned across rows — see the em-input-row-icon-alignment note.

import React, { useCallback } from "react";
import {
  Stack,
  Group,
  Grid,
  TextInput,
  NumberInput,
  Switch,
  ActionIcon,
  Tooltip,
  Button,
  Text,
  Box,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CouponTypeItemInput, InventoryItem } from "@/utils/api";
import { useOptionalInventoryItemsContext } from "@/contexts";
import { AppIcon } from "@/components/generic";
import { InventoryItemSelect } from "@/components/features/Inventory/InventoryItemSelect/InventoryItemSelect";

export interface VoucherItemsEditorProps {
  value: CouponTypeItemInput[];
  onChange: (items: CouponTypeItemInput[]) => void;
  disabled?: boolean;
  error?: string | null;
}

const emptyItem = (): CouponTypeItemInput => ({
  name: "",
  quantity: 1,
  unitCost: null,
  isOptional: false,
  inventoryItemId: null,
});

const toQuantity = (value: number | string): number => {
  const n = Number(value);

  return Number.isFinite(n) && n >= 1 ? Math.round(n) : 1;
};

const toCost = (value: number | string): number | null => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const n = Number(value);

  return Number.isFinite(n) ? n : null;
};

export const VoucherItemsEditor: React.FC<VoucherItemsEditorProps> = ({
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation(["vouchers", "common"]);

  // Only offered where an InventoryItemsProvider is mounted (the Vouchers library
  // page + the event Coupons page). Absent elsewhere → the stock mapping is hidden.
  const inventoryCtx = useOptionalInventoryItemsContext();

  const updateAt = useCallback(
    (index: number, patch: Partial<CouponTypeItemInput>) => {
      onChange(value.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    },
    [value, onChange]
  );

  const removeAt = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const addItem = useCallback(() => {
    onChange([...value, emptyItem()]);
  }, [value, onChange]);

  return (
    <Stack gap="xs">
      {value.length > 0 && (
        <Grid gutter="xs" align="center" px={2}>
          <Grid.Col span={5}>
            <Text size="xs" c="dimmed" fw={600}>
              {t("vouchers:items.columns.name", { defaultValue: "Item" })}
            </Text>
          </Grid.Col>
          <Grid.Col span={2}>
            <Text size="xs" c="dimmed" fw={600}>
              {t("vouchers:items.columns.quantity", { defaultValue: "Qty" })}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" c="dimmed" fw={600}>
              {t("vouchers:items.columns.unitCost", { defaultValue: "Vendor cost" })}
            </Text>
          </Grid.Col>
          <Grid.Col span={2} />
        </Grid>
      )}

      {value.map((item, index) => (
        <Stack key={index} gap={4}>
        <Grid gutter="xs" align="center">
          <Grid.Col span={5}>
            <TextInput
              placeholder={t("vouchers:items.fields.name.placeholder", { defaultValue: "e.g. Coffee" })}
              value={item.name ?? ""}
              onChange={(e) => updateAt(index, { name: e.currentTarget.value })}
              disabled={disabled}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <NumberInput
              value={item.quantity ?? 1}
              onChange={(v) => updateAt(index, { quantity: toQuantity(v) })}
              disabled={disabled}
              min={1}
              allowNegative={false}
              allowDecimal={false}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <NumberInput
              placeholder={t("vouchers:items.fields.unitCost.placeholder", { defaultValue: "—" })}
              value={item.unitCost ?? ""}
              onChange={(v) => updateAt(index, { unitCost: toCost(v) })}
              disabled={disabled}
              min={0}
              allowNegative={false}
              decimalScale={2}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <Group gap={4} wrap="nowrap" justify="flex-end">
              <Tooltip
                label={t("vouchers:items.fields.optional.tooltip", {
                  defaultValue: "Staff-selectable at redemption",
                })}
                withArrow
                position="top"
              >
                <Switch
                  size="md"
                  checked={item.isOptional ?? false}
                  onChange={(e) => updateAt(index, { isOptional: e.currentTarget.checked })}
                  disabled={disabled}
                  aria-label={t("vouchers:items.fields.optional.label", { defaultValue: "Optional" })}
                />
              </Tooltip>
              <Tooltip label={t("common:buttons.remove", { defaultValue: "Remove" })} withArrow position="top">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => removeAt(index)}
                  disabled={disabled}
                  aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
                >
                  <AppIcon iconName="Trash" size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Grid.Col>
        </Grid>

        {inventoryCtx && (
          <InventoryItemSelect
            label=""
            description={t("vouchers:items.fields.inventory.description", {
              defaultValue: "Optional: redeeming this line draws the quantity down from stock.",
            })}
            value={item.inventoryItemId ?? null}
            onChange={(picked: InventoryItem | null) =>
              updateAt(index, { inventoryItemId: picked?.id ?? null })
            }
            disabled={disabled}
          />
        )}
        </Stack>
      ))}

      {value.length === 0 && (
        <Box>
          <Text size="sm" c="dimmed">
            {t("vouchers:items.empty", {
              defaultValue: "No items yet — add what this voucher gives the holder.",
            })}
          </Text>
        </Box>
      )}

      {error && (
        <Text size="md" c="red">
          {error}
        </Text>
      )}

      <Group>
        <Button
          variant="light"
          size="md"
          onClick={addItem}
          disabled={disabled}
          leftSection={<AppIcon iconName="Plus" size={14} />}
        >
          {t("vouchers:items.add", { defaultValue: "Add item" })}
        </Button>
      </Group>
    </Stack>
  );
};
